import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import StoryMedia from '../stories/StoryMedia';
import useStoryboard from './useStoryboard';
import useUserPref from '../../hooks/useUserPref';
import { SKIP_RESET_MS, videoSkipTiers, nextSkip, addTravel, burstLabel, perTapLabel, tiersLine } from '../../utils/skipRamp';
import {
  HOLD_MS, BAR_HIDE_MS, COACH_DELAY_MS, SCRUB_DEAD_PX, PREVIEW_W, VIDEO_TIPS_PREF,
  tapZone, isPortrait, clampSeek, previewLeft, fmtClock, bottomClearance,
} from './storyVideoModel';

const MONO = "'JetBrains Mono', ui-monospace, monospace";

/**
 * The story video player (messages handoff Part A §4b, rolled out by Part D): the ONE player
 * for any video opened into a full-screen / story-style view — album items, video messages,
 * view-once clips, Portal frames, the event gallery. Playback is still `StoryMedia` (HLS off
 * Cloudflare Stream; this adds no second loader). On top of it this owns:
 *
 * - the tap zones: edge 20% = previous / next ITEM (handed to the host), inner 30% halves =
 *   rewind / forward on the tap ramp (`utils/skipRamp.js`); hold = pause;
 * - the seek feedback pill (`+18S` · `10S PER TAP · MAX`);
 * - the auto-hiding scrub bar behind a 32px touch strip, with the storyboard thumbnail
 *   preview (`useStoryboard`) and the time pill while dragging;
 * - portrait `cover` / landscape `contain`;
 * - the first-video tips overlay, seen once per account (`videoTips.seen`, a server pref
 *   mirrored to localStorage) across every host.
 *
 * The host keeps its own chrome — progress segments, header, reply bar, close — and draws
 * progress from `onTime(t, duration)`, both in WINDOW seconds (a trimmed clip is only its
 * window). `bottomInset` is the height of whatever the host puts along the bottom (a reply
 * bar) so the strip sits above it — the host's CHROME only: the player adds the system bar's
 * own clearance (`env(safe-area-inset-bottom)`) on top of it itself, because a scrub bar at
 * `bottom: 0` of a full-screen surface is drawn under Android's navigation buttons and every
 * tap on it goes to the system instead of to us. A host that has ALREADY lifted the player's
 * own box off that strip (a flex column with `paddingBottom: env(…)`, as the video-message and
 * view-once viewers have) passes `systemBarInset={false}` so it isn't counted twice. `tapZones={false}` lets a host that already owns the
 * frame's gestures (the Portals' StoryViewer: hold, swipe, two-zone taps) route centre taps
 * through `ref.current.skip(dir)` instead.
 *
 *   seekable  'all' (default) · 'forward' (a view-once clip: no rewind, no scrub bar) · 'none'
 *   tiers     override the tiers (Portal clips are ≤ 30 s, so `[3, 5]`)
 */
const StoryVideoPlayer = forwardRef(function StoryVideoPlayer({
  item,
  active = true,
  muted = true,
  paused = false,
  onPrev = null,
  onNext = null,
  onEnded = null,
  onTime = null,
  onDuration = null,
  onError = null,
  onHoldChange = null,
  onCoachChange = null,
  bottomInset = 0,
  systemBarInset = true,
  edgeZones = true,
  tapZones = true,
  keyboard = true,
  seekable = 'all',
  tiers: tiersProp = null,
  tips = true,
  hoverBar = false,
  fit: fitProp = null,
  /** Start over at the end instead of ending (a lone video message keeps looping, as before). */
  loop = false,
  testId = 'story-video-player',
}, ref) {
  const mediaRef = useRef(null);
  const stageRef = useRef(null);
  const trackRef = useRef(null);
  const [fileDur, setFileDur] = useState(item?.durationSec || 0);
  const [dims, setDims] = useState({ w: item?.width || 0, h: item?.height || 0 });
  const [t, setT] = useState(0);
  const tRef = useRef(0);
  const [holding, setHolding] = useState(false);
  const [seekFx, setSeekFx] = useState(null);        // { dir, total, step, max }
  const [bar, setBar] = useState(false);
  const [scrub, setScrub] = useState(null);          // 0..1 while dragging
  const [coach, setCoach] = useState(false);
  const [tipsSeen, setTipsSeen] = useUserPref(VIDEO_TIPS_PREF);
  const chain = useRef(null);
  const seekTimer = useRef(null);
  const barTimer = useRef(null);
  const holdTimer = useRef(null);
  const coachTimer = useRef(null);
  const down = useRef(null);
  const endedFor = useRef(null);
  const storyboard = useStoryboard(item?.storyboardUrl || null);

  // ── The window: a trimmed clip is only its slice. Times in and out are window seconds. ──
  const start = (item?.trimStartMs || 0) / 1000;
  const end = item?.trimEndMs > item?.trimStartMs ? item.trimEndMs / 1000 : (fileDur || 0);
  const dur = Math.max(0, (end || 0) - start) || item?.durationSec || 0;
  const tiers = useMemo(() => tiersProp || videoSkipTiers(dur), [tiersProp, dur]);
  const canRewind = seekable === 'all';
  const canForward = seekable !== 'none';
  const scrubbable = seekable === 'all';
  const portrait = isPortrait(dims.w, dims.h);
  const fit = fitProp || (portrait ? 'cover' : 'contain');
  const halted = paused || holding || scrub != null || coach;

  // A new item: fresh clock, chain, bar, hold; the coach may come back for a new video.
  const itemKey = item?.mediaUrl || item?.id || null;
  useEffect(() => {
    setT(0); tRef.current = 0;
    setFileDur(item?.durationSec || 0);
    setDims({ w: item?.width || 0, h: item?.height || 0 });
    setSeekFx(null); chain.current = null; clearTimeout(seekTimer.current);
    setBar(false); clearTimeout(barTimer.current);
    setScrub(null); setHolding(false); clearTimeout(holdTimer.current);
    endedFor.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemKey]);
  useEffect(() => () => { clearTimeout(seekTimer.current); clearTimeout(barTimer.current); clearTimeout(holdTimer.current); clearTimeout(coachTimer.current); }, []);
  useEffect(() => { onHoldChange?.(holding); }, [holding, onHoldChange]);
  useEffect(() => { onCoachChange?.(coach); }, [coach, onCoachChange]);

  // ── First-video tips: once per account, anywhere. The clip waits under the overlay. ──
  useEffect(() => {
    clearTimeout(coachTimer.current);
    setCoach(false);
    if (!tips || tipsSeen || !itemKey || !active) return undefined;
    coachTimer.current = setTimeout(() => setCoach(true), COACH_DELAY_MS);
    return () => clearTimeout(coachTimer.current);
  }, [tips, tipsSeen, itemKey, active]);
  const dismissCoach = useCallback(() => { setCoach(false); if (!tipsSeen) setTipsSeen(true); }, [tipsSeen, setTipsSeen]);

  // ── The clock, from the element, in window seconds. The window's end ends the item. ──
  const handleTime = useCallback((ct, fileDuration) => {
    if (Number.isFinite(fileDuration) && fileDuration > 0 && fileDuration !== fileDur) setFileDur(fileDuration);
    const windowT = Math.max(0, ct - start);
    tRef.current = windowT;
    setT(windowT);
    const d = (item?.trimEndMs > item?.trimStartMs ? item.trimEndMs / 1000 : fileDuration) - start;
    onTime?.(windowT, d > 0 ? d : dur);
    if (item?.trimEndMs > item?.trimStartMs && ct >= item.trimEndMs / 1000 - 0.06) {
      if (loop) { mediaRef.current?.seek?.(start); return; }
      if (endedFor.current !== itemKey) { endedFor.current = itemKey; onEnded?.(); }
    }
  }, [start, dur, fileDur, item?.trimEndMs, item?.trimStartMs, itemKey, onTime, onEnded, loop]);
  const handleEnded = useCallback(() => {
    if (endedFor.current === itemKey) return;
    endedFor.current = itemKey;
    onEnded?.();
  }, [itemKey, onEnded]);
  const handleDuration = useCallback((d) => {
    if (Number.isFinite(d) && d > 0) setFileDur(d);
    const windowD = item?.trimEndMs > item?.trimStartMs ? (item.trimEndMs - (item.trimStartMs || 0)) / 1000 : d;
    if (windowD > 0) onDuration?.(windowD);
  }, [item?.trimEndMs, item?.trimStartMs, onDuration]);

  // ── Seeking ──
  const revealBar = useCallback((hideAfter = BAR_HIDE_MS) => {
    if (!scrubbable) return;
    setBar(true);
    clearTimeout(barTimer.current);
    if (hideAfter) barTimer.current = setTimeout(() => setBar(false), hideAfter);
  }, [scrubbable]);
  const seekTo = useCallback((windowSec) => {
    const landed = clampSeek(windowSec, dur);
    mediaRef.current?.seek?.(start + landed);
    tRef.current = landed;
    setT(landed);
    onTime?.(landed, dur);
    return landed;
  }, [dur, start, onTime]);
  const skip = useCallback((dir) => {
    if (coach) return;
    if (dir < 0 ? !canRewind : !canForward) return;
    const now = Date.now();
    let c = nextSkip(chain.current, now, tiers, dir);
    const before = tRef.current;
    const landed = seekTo(before + dir * c.step);
    c = addTravel(c, landed - before);
    chain.current = c;
    setSeekFx({ dir: c.dir, total: c.total, step: c.step, max: c.max });
    revealBar(BAR_HIDE_MS);
    clearTimeout(seekTimer.current);
    seekTimer.current = setTimeout(() => { chain.current = null; setSeekFx(null); }, SKIP_RESET_MS);
  }, [coach, canRewind, canForward, tiers, seekTo, revealBar]);

  useImperativeHandle(ref, () => ({
    skip,
    seek: seekTo,
    revealBar,
    element: () => mediaRef.current?.element?.() || null,
    coachOpen: () => coach,
  }), [skip, seekTo, revealBar, coach]);

  // ── Taps and holds on the stage (when this player owns them) ──
  const onStageDown = (e) => {
    if (!tapZones || coach) return;
    const box = stageRef.current?.getBoundingClientRect();
    down.current = { at: Date.now(), fx: box && box.width ? (e.clientX - box.left) / box.width : 0.5 };
    clearTimeout(holdTimer.current);
    holdTimer.current = setTimeout(() => setHolding(true), HOLD_MS);
  };
  const onStageUp = () => {
    clearTimeout(holdTimer.current);
    const d = down.current;
    down.current = null;
    if (!d) return;
    if (holding) { setHolding(false); return; }
    if (Date.now() - d.at >= HOLD_MS) return;
    const zone = tapZone(d.fx, edgeZones && !!(onPrev || onNext));
    if (zone === 'prev') onPrev?.();
    else if (zone === 'next') onNext?.();
    else if (zone === 'rewind') skip(-1);
    else skip(1);
  };

  // ── The scrub strip: reveal on touch, scrub once the finger moves, seek on release. ──
  const fracAt = (clientX) => {
    const r = trackRef.current?.getBoundingClientRect();
    if (!r || !r.width) return 0;
    return Math.min(1, Math.max(0, (clientX - r.left) / r.width));
  };
  const onStripDown = (e) => {
    if (!scrubbable || coach) return;
    e.preventDefault(); e.stopPropagation();
    const wasShown = bar;
    const x0 = e.clientX;
    let live = wasShown;
    revealBar(0);
    const begin = (x) => { const f = fracAt(x); setScrub(f); storyboard.prefetch(f * dur); };
    if (live) begin(x0);
    const move = (ev) => {
      if (!live && Math.abs(ev.clientX - x0) > SCRUB_DEAD_PX) live = true;
      if (live) begin(ev.clientX);
    };
    const up = (ev) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      if (live) seekTo(fracAt(ev.clientX) * dur);
      setScrub(null);
      revealBar(BAR_HIDE_MS);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  // ── Keyboard: J / L ride the same ramp; the coach takes Esc / Enter / Space first. ──
  useEffect(() => {
    if (!keyboard) return undefined;
    const onKey = (e) => {
      if (e.target && /^(INPUT|TEXTAREA)$/.test(e.target.tagName)) return;
      if (coach) {
        if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); dismissCoach(); }
        return;
      }
      if (e.key === 'j' || e.key === 'J') skip(-1);
      else if (e.key === 'l' || e.key === 'L') skip(1);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [keyboard, coach, skip, dismissCoach]);

  if (!item) return null;

  const frac = scrub != null ? scrub : (dur > 0 ? Math.min(1, t / dur) : 0);
  // The host's chrome plus the system bar's own strip — see `bottomClearance`.
  const stripBottom = bottomClearance(bottomInset, systemBarInset);
  const showBar = scrubbable && (bar || scrub != null);
  const frame = scrub != null ? storyboard.frameAt(scrub * dur) : null;
  const mediaItem = { ...item, kind: 'video' };
  const coachZones = (edgeZones && (onPrev || onNext)) ? [
    { key: 'prev', label: 'BACK', sub: 'LAST ITEM', icon: 'M15 5l-7 7 7 7', w: 20 },
    { key: 'rewind', label: 'REWIND', sub: tiersLine(tiers), icon: 'M11 6l-6 6 6 6M19 6l-6 6 6 6', w: 30 },
    { key: 'forward', label: 'FORWARD', sub: tiersLine(tiers), icon: 'M13 6l6 6-6 6M5 6l6 6-6 6', w: 30 },
    { key: 'next', label: 'NEXT', sub: 'NEXT ITEM', icon: 'M9 5l7 7-7 7', w: 20 },
  ] : [
    { key: 'rewind', label: 'REWIND', sub: tiersLine(tiers), icon: 'M11 6l-6 6 6 6M19 6l-6 6 6 6', w: 50 },
    { key: 'forward', label: 'FORWARD', sub: tiersLine(tiers), icon: 'M13 6l6 6-6 6M5 6l6 6-6 6', w: 50 },
  ];
  const rings = coachZones.length === 4
    ? [[10, 0], [35, 1.2], [65, 2.4], [65, 2.75], [65, 3.1], [90, 4.4]]
    : [[25, 0.6], [75, 2.4], [75, 2.75], [75, 3.1]];

  return (
    <div ref={stageRef} data-testid={testId} data-fit={fit} data-portrait={portrait || undefined} className="absolute inset-0" style={{ overflow: 'hidden' }}>
      <StoryMedia
        ref={mediaRef}
        item={mediaItem}
        active={active}
        muted={muted}
        paused={halted}
        fit={fit}
        loop={loop}
        windowLoop={false}
        onEnded={handleEnded}
        onError={onError}
        onDuration={handleDuration}
        onDimensions={(w, h) => setDims({ w, h })}
        onTime={handleTime}
      />

      {tapZones && (
        <div
          data-testid="story-video-tapzones"
          onPointerDown={onStageDown}
          onPointerUp={onStageUp}
          onPointerCancel={onStageUp}
          style={{ position: 'absolute', inset: 0, zIndex: 5, cursor: 'pointer', touchAction: 'none' }}
        />
      )}

      {/* The skip actions exist for assistive tech even though sighted use is the tap zones. */}
      <div style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', clipPath: 'inset(50%)', whiteSpace: 'nowrap' }}>
        {canRewind && <button type="button" onClick={() => skip(-1)}>Rewind</button>}
        {canForward && <button type="button" onClick={() => skip(1)}>Forward</button>}
      </div>

      {seekFx && (
        <div data-testid="story-video-seek" aria-live="polite" style={{ position: 'absolute', top: '50%', left: seekFx.dir < 0 ? '30%' : '70%', transform: 'translate(-50%, -50%)', zIndex: 6, pointerEvents: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, color: '#fff' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, height: 52, padding: '0 18px', background: 'rgba(0,0,0,.72)', borderRadius: 999, fontFamily: MONO, fontSize: 22, fontWeight: 800, letterSpacing: '.02em' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ transform: seekFx.dir < 0 ? 'scaleX(-1)' : 'none' }}><path d="M3 5l9 7-9 7zM12 5l9 7-9 7z" /></svg>
            {burstLabel(seekFx)}
          </span>
          <span style={{ height: 22, padding: '0 10px', display: 'flex', alignItems: 'center', background: '#fff', color: '#000', borderRadius: 999, fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: '.12em' }}>{perTapLabel(seekFx)}</span>
        </div>
      )}

      {/* The bottom scrim only shows with the visible bar (the host's reply field brings its own). */}
      {scrubbable && (
        <div aria-hidden style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: `calc(120px + ${stripBottom})`, zIndex: 6, pointerEvents: 'none', background: 'linear-gradient(to top, rgba(0,0,0,.7), rgba(0,0,0,0))', opacity: showBar ? 1 : 0, transition: 'opacity 200ms linear' }} />
      )}

      {scrubbable && !coach && (
        <div
          data-testid="story-video-strip"
          data-no-tap
          data-visible={showBar || undefined}
          className={hoverBar ? 'pr-video-strip-hover' : undefined}
          onPointerDown={onStripDown}
          style={{ position: 'absolute', left: 12, right: 12, bottom: stripBottom, height: 32, zIndex: 8, touchAction: 'none', cursor: 'pointer' }}
        >
          {scrub != null && (
            <div data-testid="story-video-preview" style={{ position: 'absolute', bottom: 52, left: previewLeft(scrub), transform: 'translateX(-50%)', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, pointerEvents: 'none' }}>
              <div style={{ position: 'relative', width: PREVIEW_W, aspectRatio: '9 / 16', border: '2px solid #fff', borderRadius: 8, background: '#000', overflow: 'hidden', boxSizing: 'border-box' }}>
                {frame ? (
                  frame.xywh ? (
                    <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${frame.url})`, backgroundRepeat: 'no-repeat', backgroundPosition: `-${frame.xywh.x}px -${frame.xywh.y}px`, backgroundSize: 'auto', transform: `scale(${PREVIEW_W / frame.xywh.w})`, transformOrigin: 'top left' }} />
                  ) : (
                    <img src={frame.url} alt="" draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                  )
                ) : item.thumbnailUrl ? (
                  <img src={item.thumbnailUrl} alt="" draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.6 }} />
                ) : null}
              </div>
              <span style={{ height: 24, padding: '0 10px', display: 'flex', alignItems: 'center', background: '#fff', color: '#000', borderRadius: 999, fontFamily: MONO, fontSize: 12, fontWeight: 700, letterSpacing: '.04em' }}>{fmtClock(scrub * dur)} / {fmtClock(dur)}</span>
            </div>
          )}
          <div
            ref={trackRef}
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={Math.round(dur)}
            aria-valuenow={Math.round(frac * dur)}
            aria-valuetext={`${fmtClock(frac * dur)} of ${fmtClock(dur)}`}
            tabIndex={-1}
            style={{ position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', height: scrub != null ? 6 : 3, background: 'rgba(255,255,255,.3)', borderRadius: 999, opacity: showBar ? 1 : 0, transition: 'opacity 200ms linear, height 120ms' }}
          >
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${(frac * 100).toFixed(2)}%`, background: '#fff', borderRadius: 999 }} />
            <div style={{ position: 'absolute', left: `${(frac * 100).toFixed(2)}%`, top: '50%', width: scrub != null ? 18 : 12, height: scrub != null ? 18 : 12, transform: 'translate(-50%, -50%)', background: '#fff', borderRadius: '50%', transition: 'width 120ms, height 120ms' }} />
          </div>
        </div>
      )}

      {coach && (
        <div role="dialog" aria-label="How the video player works" data-testid="story-video-coach" className="pr-video-coach" style={{ position: 'absolute', inset: 0, zIndex: 20, background: 'rgba(0,0,0,.82)', color: '#fff', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '22px 18px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: '.16em', color: 'rgba(255,255,255,.75)' }}>[ VIDEO CONTROLS ]</span>
            <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 20, fontWeight: 700, lineHeight: 1.2, textWrap: 'pretty' }}>Tap the middle to skip. Tap faster to skip further.</span>
          </div>
          <div style={{ position: 'relative', flex: 1, display: 'grid', gridTemplateColumns: coachZones.map((z) => `${z.w}fr`).join(' '), margin: '18px 0 0' }}>
            {coachZones.map((z, i) => (
              <div key={z.key} style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, borderLeft: i === 0 ? 0 : '2px dashed rgba(255,255,255,.35)', textAlign: 'center', padding: '0 4px' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square"><path d={z.icon} /></svg>
                <span style={{ fontFamily: MONO, fontSize: 11, fontWeight: 800, letterSpacing: '.1em' }}>{z.label}</span>
                <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: '.08em', lineHeight: 1.5, color: 'rgba(255,255,255,.75)' }}>{z.sub}</span>
              </div>
            ))}
            <div aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              {rings.map(([x, d], i) => (
                <span key={i} className="pr-video-tap-ring" style={{ left: `${x}%`, animationDelay: `${d}s` }} />
              ))}
            </div>
          </div>
          <div style={{ padding: '0 18px calc(18px + env(safe-area-inset-bottom, 0px))', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: '.1em', lineHeight: 1.5 }}>
              <span>HOLD ANYWHERE TO PAUSE</span>
              {scrubbable && <span>TOUCH THE BOTTOM EDGE, THEN DRAG TO PREVIEW</span>}
            </div>
            {scrubbable && (
              <div aria-hidden style={{ position: 'relative', height: 3, background: 'rgba(255,255,255,.3)', borderRadius: 999 }}>
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '38%', background: '#fff', borderRadius: 999 }} />
                <div style={{ position: 'absolute', left: '38%', top: '50%', width: 18, height: 18, transform: 'translate(-50%, -50%)', background: '#fff', borderRadius: '50%' }} />
              </div>
            )}
            <button type="button" onClick={dismissCoach} className="hover:bg-black hover:text-white" style={{ height: 44, border: '2px solid #fff', borderRadius: 999, background: '#fff', color: '#000', fontFamily: MONO, fontSize: 11, fontWeight: 800, letterSpacing: '.14em', cursor: 'pointer', transition: 'background-color 200ms, color 200ms' }}>GOT IT</button>
          </div>
        </div>
      )}
    </div>
  );
});

export default StoryVideoPlayer;
